import { ModalLayer } from "./Sheet";
import React from "react";
import { Crown, Check, X, ChevronLeft, ExternalLink } from "lucide-react";

// Портал управления подпиской/настройками.
export const PREMIUM_URL = "https://sns.abm-system.ru/settings";

export const PREMIUM_FEATURES: { title: string; desc: string }[] = [
  { title: "Безлимитные запросы по ИНН", desc: "Без дневного лимита анализа контрагентов (в бесплатном доступе — 10 ИНН в день)." },
  { title: "Конструктор методик", desc: "Свои методики оценки: показатели → пороги-баллы → рейтинг. Формулы как в Excel." },
  { title: "Банковские методики", desc: "Каталог методик банков (Почта Банк, Пересвет, Уралсиб, Банк СОЮЗ, Совкомбанк…) с расчётом по клиенту." },
  { title: "Квартальная отчётность", desc: "Загрузка и хранение поквартальных данных + столбец в сравнении." },
  { title: "Опыт клиента", desc: "Учёт исполненных контрактов и статусов для более точной оценки." },
];

// Полноэкранная промо-страница премиума.
export function PremiumPromo({ onBack }: { onBack: () => void }) {
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-5 pt-8 w-full">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-extrabold font-display text-slate-900 tracking-tight flex items-center gap-2">
          <Crown className="w-6 h-6 text-amber-500" /> Премиум-доступ
        </h2>
        <button onClick={onBack}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-display text-xs uppercase font-bold tracking-wider py-2 px-4 rounded-xl shadow-sm transition-colors cursor-pointer inline-flex items-center gap-1 shrink-0">
          <ChevronLeft className="w-4 h-4" /> На главную
        </button>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-100 rounded-2xl p-6 mb-5">
        <p className="text-sm text-slate-700 leading-relaxed">
          Премиум открывает полный инструментарий СЭБ Anyskin: безлимитный анализ контрагентов,
          собственные и банковские методики оценки, работу с отчётностью и опытом клиента.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {PREMIUM_FEATURES.map((f, i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl shadow-sm p-4 flex items-start gap-3">
            <span className="mt-0.5 w-6 h-6 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <Check className="w-4 h-4" />
            </span>
            <div>
              <div className="font-bold text-sm text-slate-900">{f.title}</div>
              <p className="text-xs text-slate-500 mt-0.5 leading-snug">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <a href={PREMIUM_URL} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl cursor-pointer shadow-sm">
        <Crown className="w-5 h-5" /> Оформить премиум <ExternalLink className="w-4 h-4" />
      </a>
      <p className="t-micro text-slate-400 mt-3">Подключение и оплата — в личном кабинете ABM. После активации все разделы откроются автоматически.</p>
    </main>
  );
}

// Модалка-апселл (лимит бесплатного доступа или клик по премиум-разделу).
export function PremiumModal({ open, onClose, onMore, message }:
  { open: boolean; onClose: () => void; onMore: () => void; message?: string | null }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-(--z-sheet) bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <ModalLayer onClose={onClose} />
      <div role="dialog" aria-modal="true" className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md overflow-hidden pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-amber-400 to-amber-500 px-6 py-5 text-white relative">
          <button onClick={onClose} aria-label="Закрыть" className="absolute top-1 right-1 grid place-items-center w-11 h-11 text-white/80 hover:text-white cursor-pointer"><X className="w-5 h-5" /></button>
          <Crown className="w-8 h-8 mb-2" />
          <h3 className="font-extrabold font-display text-lg leading-tight">Премиум-функция</h3>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-700">{message || "Этот раздел доступен в премиум-доступе."}</p>
          <ul className="space-y-1.5">
            {PREMIUM_FEATURES.slice(0, 4).map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <Check className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {f.title}
              </li>
            ))}
          </ul>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer">Закрыть</button>
          <button onClick={onMore} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg cursor-pointer">
            <Crown className="w-4 h-4" /> Подробнее о премиум
          </button>
        </div>
      </div>
    </div>
  );
}
